import { CenteredLayout } from '@/components/layouts/CenteredLayout';
import {
  TARA_CALLBACK_ROUTE,
  TARA_RESULT_TOKEN_PARAM,
} from '@/features/auth/taraConfig';
import { useAuth } from '@/hooks/useAuth';
import { usePageTitle } from '@/hooks/usePageTitle';
import { TransText } from '@/i18n/trans/text';
import { getTaraReturnPath, setTaraReturnPath } from '@/utils/auth';
import { Button, Spinner, Typography } from '@packages/webshop-design-system';
import { type FC, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';

/**
 * Renders after TARA → BE → FE. Reads the one-time login-result token from the
 * URL, scrubs it, exchanges it, and forks to the app (existing user) or to
 * registration (new user). See BR-56868.
 */
const TaraCallback: FC = () => {
  const navigate = useNavigate();
  const { loginWithTara } = useAuth();
  const [hasError, setHasError] = useState(false);
  // Guard against double-invoke (React StrictMode / re-render) firing the
  // one-time token exchange twice.
  const ran = useRef(false);

  usePageTitle('logIn');

  useEffect(() => {
    if (ran.current) return;
    ran.current = true;

    const params = new URLSearchParams(window.location.search);
    const loginResultToken = params.get(TARA_RESULT_TOKEN_PARAM);

    // Scrub the token from the URL right away — no token in the address bar or
    // browser history for the user to copy or leak.
    window.history.replaceState({}, '', TARA_CALLBACK_ROUTE);

    if (!loginResultToken) {
      setHasError(true);
      return;
    }

    loginWithTara(loginResultToken)
      .then((result) => {
        if (result.kind === 'authenticated') {
          const returnPath = getTaraReturnPath();
          setTaraReturnPath(null);
          navigate(returnPath || '/', { replace: true });
        } else {
          navigate('/signup', {
            replace: true,
            state: {
              registrationToken: result.registrationToken,
              firstName: result.firstName,
              lastName: result.lastName,
              birthDate: result.birthDate,
            },
          });
        }
      })
      .catch(() => setHasError(true));
  }, [loginWithTara, navigate]);

  if (hasError) {
    return (
      <CenteredLayout>
        <div className="mx-auto flex w-full max-w-[410px] flex-col items-center gap-6 p-4">
          <Typography variant="body1" className="text-center">
            <TransText i18nKey="somethingWentWrongSigningIn" />
          </Typography>
          <Button
            data-testid="tara-callback-retry-button"
            size="medium"
            onClick={() => navigate('/login', { replace: true })}
          >
            <TransText i18nKey="tryAgain" />
          </Button>
        </div>
      </CenteredLayout>
    );
  }

  return (
    <CenteredLayout>
      <div className="mx-auto flex w-full flex-col items-center gap-4 p-4">
        <Spinner size={40} />
        <Typography variant="body1">
          <TransText i18nKey="signingYouIn" />
        </Typography>
      </div>
    </CenteredLayout>
  );
};

export default TaraCallback;
